<template>

    <div id="settings">

        <div class="flex">
            <div class="flex-1">
                <h1 class="text-2xl font-medium text-gray-900 capitalize">Settings</h1>
            </div>

            <router-link :to="{ name: 'invite' }" custom v-slot="{ navigate }">
                <button @click="navigate" class="btn text-sm">
                    Invite Team Member
                </button>
            </router-link>
        </div>

        <div class="flex flex-col" v-if="loaded">

            <ul class="top-help-block">
                <li><span class="text-brand-600">*</span> denotes required field</li>
            </ul>

            <form action="#" method="POST" @submit.prevent="updateSettings()">

                <div class="panel">
                    <div class="panel-form">
                        <div class="panel-title">
                            <h3>Account Information</h3>
                            <p>
                                Your company/organisation settings.
                            </p>
                        </div>
                        <div class="panel-section">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('account') }">
                                    <label for="account" class="block text-sm font-medium text-gray-700">Company Name<i></i></label>
                                    <input type="text" name="account" id="account" v-model="form.data.account">
                                    <span v-if="form.fieldHasErrors('account')">{{ form.getFieldError('account') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="panel-form mt-6">
                        <div class="panel-title">
                            <h3>Account Funds Limit</h3>
                            <p>
                                Notify me when my funds goes below this amount
                            </p>
                        </div>
                        <div class="panel-section">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('amount_threshold') }">
                                    <label for="account" class="block text-sm font-medium text-gray-700">Account Threshold Limit<i></i></label>
                                    <input type="text" name="amount_threshold" id="amount_threshold" v-model="form.data.amount_threshold">
                                    <span v-if="form.fieldHasErrors('amount_threshold')">{{ form.getFieldError('amount_threshold') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="panel-form mt-6">
                        <div class="panel-title">
                            <h3>Account Webhook URL</h3>
                            <p>
                                Notify my server when an important event happen such as order complete
                            </p>
                        </div>
                        <div class="panel-section">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('webhook_url') }">
                                    <label for="account" class="block text-sm font-medium text-gray-700">Account Webhook URL</label>
                                    <input type="text" name="webhook_url" id="webhook_url" v-model="form.data.webhook_url">
                                    <span v-if="form.fieldHasErrors('webhook_url')">{{ form.getFieldError('webhook_url') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="panel">
                    <div class="panel-form">
                        <div class="panel-title">
                            <h3>Personal Information</h3>
                            <p>
                                Your user account and privacy settings.
                            </p>
                        </div>
                        <div class="panel-section">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('name') }">
                                    <label for="first_name" class="block text-sm font-medium text-gray-700">Name<i></i></label>
                                    <input type="text" name="first_name" id="first_name" v-model="form.data.name">
                                    <span v-if="form.fieldHasErrors('name')">{{ form.getFieldError('name') }}</span>
                                </div>

                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('email') }">
                                    <label for="email_address" class="block text-sm font-medium text-gray-700">Email<i></i></label>
                                    <input type="text" name="email_address" id="email_address" v-model="form.data.email">
                                    <span v-if="form.fieldHasErrors('email')">{{ form.getFieldError('email') }}</span>
                                </div>

                                <div class="col-span-6">
                                    <div class="flex items-center">
                                        <input id="marketing" name="marketing" type="checkbox" class="h-4 w-4 text-brand-500 focus:ring-brand-500 border-gray-300 rounded" value="1" v-model="form.data.marketing">
                                        <label for="marketing" class="ml-2 block text-sm text-gray-900">
                                            Receive new offers by email?
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="panel">
                    <div class="panel-form">
                        <div class="panel-title">
                            <h3>Address / Delivery</h3>
                            <p>
                                This information will be used as a default when placing orders. This information will also
                                be displayed on invoices.
                            </p>
                        </div>
                        <div class="panel-section">
                            <Address v-model="form"></Address>
                        </div>
                    </div>
                </div>

                <div class="panel">
                    <div class="panel-form">
                        <div class="panel-title">
                            <h3>Account Security</h3>
                            <p>
                                Settings to keep you account secure.
                            </p>
                        </div>
                        <div class="panel-section">
                            <div class="grid grid-cols-6 gap-6">

                                <div class="form-control col-span-6 sm:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('new_password') }">
                                    <label for="new_password">New Password</label>
                                    <input type="password" name="new_password" id="new_password" autocomplete="new-password" v-model="form.data.new_password">
                                    <span v-if="form.fieldHasErrors('new_password')">{{ form.getFieldError('new_password') }}</span>
                                </div>

                                <div class="form-control col-span-6 sm:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('new_password_confirmation') }">
                                    <label for="new_password_confirmation">Confirm Password</label>
                                    <input type="password" name="new_password_confirmation" id="new_password_confirmation" autocomplete="new-password" v-model="form.data.new_password_confirmation">
                                    <span v-if="form.fieldHasErrors('new_password_confirmation')">{{ form.getFieldError('new_password_confirmation') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="panel">
                    <div class="panel-form">
                        <div class="panel-title">
                            <h3>Two Factor Authentication</h3>
                            <p>
                                Manage Two Factor Authentication for your account.
                            </p>
                        </div>
                        <div class="panel-section">

                            <!-- 2FA Enabled -->
                            <div class="grid grid-cols-6 gap-6" v-if="form.data.two_factor.enabled && form.data.two_factor.confirmed">
                                <p class="flex items-center col-span-6 sm:col-span-4 text-sm">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="text-brand-600 w-8 h-8 mr-2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                                    </svg>

                                    Two Factor Authentication is enabled on your account.
                                </p>

                                <div class="col-span-6 sm:col-span-4 text-sm" v-if="form.show_recovery_codes">

                                    <p class="mb-6">
                                        If you loose access to your authentication device, you can use these recovery codes to
                                        login to your account. Store them somewhere safe.
                                    </p>

                                    <ul class="bg-gray-50 text-sm text-gray-600 text-center p-2">
                                        <li v-for="code in form.data.two_factor.rc">{{ code }}</li>
                                    </ul>

                                    <button type="button" class="btn-outline text-sm justify-center px-6 mt-4" :disabled="form.two_factor_loading" @click.prevent="form.regenerateRecoveryCodes()">
                                        Re-generate Recovery Codes
                                    </button>
                                </div>

                                <div class="flex form-control col-span-6 sm:col-span-4">

                                    <button type="button" class="btn text-sm justify-center px-6 mr-2" :disabled="form.two_factor_loading" @click.prevent="form.showRecoveryCodes()" v-if="!form.show_recovery_codes">
                                        Show Recovery Codes
                                    </button>
                                    <button type="button" class="btn text-sm justify-center px-6 mr-2" :disabled="form.two_factor_loading" @click.prevent="form.hideRecoveryCodes()" v-else>
                                        Hide Recovery Codes
                                    </button>
                                    <ConfirmationModal button_classes="btn-error text-sm justify-center px-6" button_text="Disable Two Factor Authentication" confirm_text="Yes" close_text="No" :disabled="form.two_factor_loading" @confirmModal="form.disableTwoFactorAuthentication()">
                                        <div class="mt-3 text-center sm:mt-5">
                                            <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                                Disable Two Factor Authentication
                                            </h3>
                                            <div class="mt-2 text-sm text-gray-500">
                                                <p class="my-1">
                                                    Are you sure you would like to disable two authentication?
                                                </p>

                                                <p class="my-1">
                                                    This action will render both your authenticator and back-up codes invalid, and will allow complete access
                                                    to your account using only it's password.
                                                </p>
                                            </div>
                                        </div>
                                    </ConfirmationModal>
                                </div>
                            </div>

                            <!-- 2FA Setup -->
                            <div class="xl:flex" v-if="form.data.two_factor.enabled && form.data.two_factor.confirmed === null">
                                <div class="xl:w-1/3 xl:pt-6 mx-auto w-64 p-4">
                                    <div id="qrcode_2fa" v-if="form.data.two_factor.qr" v-html="form.data.two_factor.qr"></div>

                                    <div class="text-center mt-3 text-sm font-medium">
                                        {{ form.data.two_factor.token }}
                                    </div>

                                    <div class="form-control mt-3 text-center">
                                        <button type="button" class="btn-error text-sm justify-center px-6" :disabled="form.two_factor_loading" @click.prevent="form.disableTwoFactorAuthentication()">
                                            Cancel Two Factor Setup
                                        </button>
                                    </div>
                                </div>

                                <div class="xl:w-2/3 px-6">
                                    <h4 class="font-medium text-lg">Two Factor Authentication Setup </h4>

                                    <ol class="my-2 list-decimal text-gray-600 text-sm">
                                        <li class="py-2">
                                            <span class="font-medium">Download</span> your preferred two-factor authenticator application. If you don't
                                            already have one, we recommend <a href="https://support.google.com/accounts/answer/1066447" target="_blank">Google Authenticator</a>.
                                        </li>
                                        <li class="py-2">
                                            Use your application to <span class="font-medium">scan the QR code</span>.
                                        </li>
                                        <li class="py-2">
                                            Enter the 6-digit code provided by your app and click 'Finish Two Factor Setup' to verify.
                                        </li>
                                    </ol>

                                    <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('code') }">
                                        <input type="text" name="code" id="code" placeholder="123456" maxlength="6" v-model="form.code_2fa">
                                        <span v-if="form.fieldHasErrors('code')">{{ form.getFieldError('code') }}</span>
                                    </div>

                                    <div class="form-control mt-6 text-right">
                                        <button type="button" class="btn text-sm justify-center px-6" :disabled="form.two_factor_loading" @click.prevent="form.checkTwoFactorAuthentication()">
                                            Finish Two Factor Setup
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Setup/Enable 2FA -->
                            <div class="grid grid-cols-6 gap-6" v-if="!form.data.two_factor.enabled">
                                <p class="flex items-center col-span-6 sm:col-span-4 text-sm">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="text-error-500 w-8 h-8 mr-2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286zm0 13.036h.008v.008H12v-.008z" />
                                    </svg>

                                    You currently do not have two-factor authentication enabled.
                                </p>

                                <div class="form-control col-span-6 sm:col-span-4">
                                    <button type="button" class="btn text-sm justify-center px-6" :disabled="form.two_factor_loading" @click.prevent="form.enableTwoFactorAuthentication()">
                                        Configure Two Factor Authentication
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                <div class="panel">
                    <div class="panel-form">
                        <div class="panel-title">
                            <h3>Delete Account</h3>
                            <p>
                                Deleting your account will prevent you
                                from logging in.
                            </p>
                        </div>
                        <div class="panel-section">
                            <div class="grid grid-cols-6 gap-6">

                                <div class="form-control col-span-6 sm:col-span-4">
                                    <ConfirmationModal button_classes="btn-error text-sm justify-center px-6" button_text="Delete Account" @confirmModal="deleteAccount">
                                        <div class="mt-3 text-center sm:mt-5">
                                            <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                                Delete Account?
                                            </h3>
                                            <div class="mt-2">
                                                <p class="text-sm text-gray-500">
                                                    Are you sure you would like to delete your account? Deleting your account will prevent you
                                                    from logging in.
                                                </p>
                                            </div>
                                        </div>
                                    </ConfirmationModal>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                <div class="flex mt-6 justify-end">

                    <div class="flex border-l-4 border-brand-500 bg-white text-gray-700 px-4 py-1 rounded tracking-wide shadow items-center text-brand-400" v-if="form.successful()">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <p class="text-gray-700 text-sm">
                            {{ form.getSuccessMessage() }}
                        </p>
                    </div>

                    <div class="flex border-l-4 border-error-500 bg-error-50 text-gray-700 px-4 py-1 rounded tracking-wide shadow items-center text-error-400" v-if="form.hasError()">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <p class="text-error-500 text-sm">
                            {{ form.getErrorMessage() }}
                        </p>
                    </div>

                    <button type="submit" class="btn justify-center px-6 ml-6" :disabled="form.isFormUpdating()">
                        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" v-if="form.isFormUpdating()">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Update Settings
                    </button>
                </div>


            </form>

        </div>
        <Loading message="Loading Settings" v-else></Loading>

    </div>

</template>

<script>
import Loading from "../Support/Loading";
import {User} from "../../app/Auth/User";
import ConfirmationModal from "../Support/ConfirmationModal";
import Address from "../Support/Address";
import {PermissionMixin} from "../../support/mixins";

export default {
    name: "Settings",
    mixins: [PermissionMixin],
    data() {
        return {
            form: null,
            loaded: false
        }
    },
    components: {
        Loading,
        Address,
        ConfirmationModal,
    },
    methods: {
        updateSettings() {
            let vm = this;

            vm.form.updateSettings().then(r => {
                // force a setup complete flag to prevent page reload and banner removal
                vm.$parent.setup_complete = true;
            }).catch(error => vm.handleError(error));
        },
        deleteAccount() {
            let vm = this;

            vm.form.delete().then(response => {
                vm.$router.go(0);
            });
        }
    },
    mounted() {
        let vm = this;

        new Promise(resolve => {
            vm.form = new User();

            vm.form.settings().then(response => {
                resolve(true);
            }).catch(error => vm.handleError(error));

        }).then(result => {
            vm.loaded = result;
        });
    }
}
</script>


